import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useSelector } from 'react-redux';

import PopupV3, { PopupTypes } from '../../Popup/PopupV3';

import styles from './RecurringSubmissionPopups.module.scss';

// Set to false to allow unchecking the primary event in the recurring publish popup.
const FORCE_PRIMARY_PUBLISHED = true;

export const initializeRecurringPublishSelection = (recurrentEvents = []) => {
    return recurrentEvents.map((recurrentEvent) => ({
        ...recurrentEvent,
        publishOnCreate: typeof recurrentEvent.publishOnCreate === 'boolean' ? recurrentEvent.publishOnCreate : true,
    }));
};

function RecurringSubmissionPopups(props) {
    const {
        showSaveDraftPopup,
        showPublishPopup,
        recurringPublishSelection,
        onConfirmSaveDraft,
        onCancelSaveDraft,
        onConfirmPublish,
        onCancelPublish,
        onToggleSelectAll,
        onToggleEvent,
        timeZone,
        isPrimaryPublished,
        onTogglePrimary,
        primaryEvent,
    } = props;

    const coreUI = useSelector(({ coreUI }) => coreUI);
    // Normalize timezone to a valid IANA string for moment.tz
    let timeZoneValue = 'UTC';

    if (typeof timeZone === 'string' || typeof timeZone === 'number') {
        const tz = coreUI.types.timeZones.find(tz => String(tz.id) === String(timeZone));
        timeZoneValue = tz?.value || tz?.name || 'UTC';
    } else if (typeof timeZone === 'object' && timeZone !== null) {
        timeZoneValue = timeZone.value || timeZone.name || 'UTC';
    }

    // Fallback safety: ensure it's a valid timezone
    if (!moment.tz.zone(timeZoneValue)) {
        timeZoneValue = coreUI.types.timeZones.find(tz => tz.id === 77)?.value || 'UTC';
    }

    const effectivePrimaryPublished = FORCE_PRIMARY_PUBLISHED || !!isPrimaryPublished;

    const allRecurringSelected =
        recurringPublishSelection.length > 0 &&
        recurringPublishSelection.every((recurrentEvent) => recurrentEvent.publishOnCreate) &&
        effectivePrimaryPublished;

    const displayEvents = [
        {
            ...primaryEvent,
            isPrimary: true,
            publishOnCreate: effectivePrimaryPublished,
            id: 'primary',
        },
        ...recurringPublishSelection.map(e => ({ ...e, isPrimary: false })),
    ];

    const formatRecurringPublishDateRange = (event) => {
        let startRaw;
        let endRaw;

        if (event.isPrimary) {
            startRaw = event?.eventSchedule?.eventStartUtc;
            endRaw = event?.eventSchedule?.eventEndUtc;
        } else {
            startRaw = event.startDate;
            endRaw = event.endDate;
        }

        const start = startRaw ? moment.utc(startRaw).tz(timeZoneValue) : null;
        const end = endRaw ? moment.utc(endRaw).tz(timeZoneValue) : null;

        if (!start || !end) {
            return '';
        }

        return `${start.format('ddd, MMM D (h:mma)')} - ${end.format('ddd, MMM D (h:mma)')}`;
    };

    const recurringPublishModalBody = (
        <div className={styles.recurringPublishPopupBody}>
            <p className={styles.recurringPublishPopupDescription}>Events you don&apos;t check will be saved as drafts.</p>
            <div className={styles.recurringPublishSelectAllRow}>
                <span>Select All</span>
                <input
                    type="checkbox"
                    checked={allRecurringSelected}
                    className={styles.recurringPublishCheckbox}
                    onChange={(e) => {
                        const { checked } = e.target;
                        onToggleSelectAll(checked);
                        if (onTogglePrimary && !FORCE_PRIMARY_PUBLISHED) {
                            onTogglePrimary(checked);
                        }
                    }}
                />
            </div>
            <div className={styles.recurringPublishList}>
                {displayEvents.map((recurrentEvent, index) => {
                    const rowKey = recurrentEvent.id || recurrentEvent.order || index + 1;
                    const { isPrimary } = recurrentEvent;
                    const rowLabel = isPrimary ? 'Primary' : `Event ${index + 1}`;
                    return (
                        <div key={`recurrence-publish-row-${rowKey}`} className={`${styles.recurringPublishRow} ${isPrimary ? styles.isPrimary : ''}`}>
                            <div className={styles.recurringPublishMain}>
                                <div className={styles.recurringPublishLabel}>{rowLabel}</div>
                                <div className={styles.recurringPublishDetails}>
                                    <p className={styles.recurringPublishName}>{recurrentEvent.name}</p>
                                    <p>{formatRecurringPublishDateRange(recurrentEvent)}</p>
                                </div>
                            </div>
                            <input
                                type="checkbox"
                                checked={isPrimary ? effectivePrimaryPublished : !!recurrentEvent.publishOnCreate}
                                disabled={isPrimary && FORCE_PRIMARY_PUBLISHED}
                                className={styles.recurringPublishCheckbox}
                                onChange={(e) => {
                                    const { checked } = e.target;
                                    if (isPrimary) {
                                        if (onTogglePrimary && !FORCE_PRIMARY_PUBLISHED) {
                                            onTogglePrimary(checked);
                                        }
                                    } else {
                                        onToggleEvent(rowKey, checked);
                                    }
                                }}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );

    return (
        <>
            <PopupV3
                title="Save as Draft?"
                message="This will save the primary event and all its recurring dates as a single draft."
                type={PopupTypes.INFO}
                isOpen={!!showSaveDraftPopup}
                onConfirmClick={onConfirmSaveDraft}
                onCancelClick={onCancelSaveDraft}
                confirmButtonLabel="Save"
                cancelButtonLabel="Cancel"
                showCancelButton
                noIcon
            />
            <PopupV3
                title="Please Confirm Which Events You Want Published"
                message={recurringPublishModalBody}
                type={PopupTypes.INFO}
                isOpen={!!showPublishPopup}
                onConfirmClick={onConfirmPublish}
                onCancelClick={onCancelPublish}
                confirmButtonLabel="Save"
                cancelButtonLabel="Cancel"
                showCancelButton
                customContent
                noIcon
                className={styles.recurringPublishPopup}
                isScrollable
            />
        </>
    );
}

RecurringSubmissionPopups.propTypes = {
    showSaveDraftPopup: PropTypes.bool,
    showPublishPopup: PropTypes.bool,
    recurringPublishSelection: PropTypes.arrayOf(PropTypes.object),
    onConfirmSaveDraft: PropTypes.func.isRequired,
    onCancelSaveDraft: PropTypes.func.isRequired,
    onConfirmPublish: PropTypes.func.isRequired,
    onCancelPublish: PropTypes.func.isRequired,
    onToggleSelectAll: PropTypes.func.isRequired,
    onToggleEvent: PropTypes.func.isRequired,
    isPrimaryPublished: PropTypes.bool,
    primaryEvent: PropTypes.shape({}),
    onTogglePrimary: PropTypes.func,
};

RecurringSubmissionPopups.defaultProps = {
    showSaveDraftPopup: false,
    showPublishPopup: false,
    recurringPublishSelection: [],
    isPrimaryPublished: true,
    primaryEvent: {},
    onTogglePrimary: () => {},
};

export default RecurringSubmissionPopups;
